import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { getCurrentIdToken } from '../services/account/authService'
import { claimDevice, PlayerApiError, updateDisplayName, type PlayerProfile } from '../services/account/playerApi'
import { getDeviceId } from '../utils/deviceUtils'
import { getGuestName } from '../utils/storage'
import { usePlayerAuth } from './PlayerAuthContext'

/**
 * How the signed-in player's profile is doing.
 *  - `idle`     nobody is signed in
 *  - `syncing`  linking this browser to the account
 *  - `ready`    linked; `profile` is set
 *  - `conflict` this browser already belongs to a different account
 *  - `error`    could not reach the backend; `retry` tries again
 */
export type PlayerStatus = 'idle' | 'syncing' | 'ready' | 'conflict' | 'error'

interface PlayerContextValue {
  status: PlayerStatus
  /** `Name #TAG`, once linked. Null for guests. */
  profile: PlayerProfile | null
  /** Changes the display name. Rejects with a `PlayerApiError` (e.g. a name that is not allowed). */
  rename: (displayName: string) => Promise<PlayerProfile>
  retry: () => void
}

const PlayerContext = createContext<PlayerContextValue | undefined>(undefined)

/**
 * Signing in *attaches* this browser to the account rather than creating a new
 * player: every visitor already plays as an anonymous device, and the account
 * just becomes the name shown for it. So as soon as someone signs in we link the
 * browser (safe to repeat), passing the guest name they already use so they keep it.
 */
export const PlayerProvider = ({ children }: { children: ReactNode }) => {
  const { user } = usePlayerAuth()
  const uid = user?.uid ?? null

  const [status, setStatus] = useState<PlayerStatus>('idle')
  const [profile, setProfile] = useState<PlayerProfile | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!uid) {
      setStatus('idle')
      setProfile(null)
      return
    }

    let cancelled = false
    setStatus('syncing')

    const link = async () => {
      try {
        const idToken = await getCurrentIdToken()
        if (!idToken) throw new PlayerApiError('AUTH_INVALID_TOKEN')

        const linked = await claimDevice({
          idToken,
          deviceId: getDeviceId(),
          suggestedName: getGuestName(),
        })
        if (cancelled) return
        setProfile(linked)
        setStatus('ready')
      } catch (error) {
        if (cancelled) return
        setProfile(null)
        setStatus(error instanceof PlayerApiError && error.code === 'PLAYER_DEVICE_CLAIMED' ? 'conflict' : 'error')
      }
    }
    void link()

    return () => {
      cancelled = true
    }
  }, [uid, attempt])

  const rename = useCallback(async (displayName: string) => {
    const idToken = await getCurrentIdToken()
    if (!idToken) throw new PlayerApiError('AUTH_INVALID_TOKEN')
    const updated = await updateDisplayName(idToken, displayName)
    setProfile(updated)
    return updated
  }, [])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  const value = useMemo<PlayerContextValue>(
    () => ({ status, profile, rename, retry }),
    [status, profile, rename, retry]
  )

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>
}

export const usePlayer = (): PlayerContextValue => {
  const context = useContext(PlayerContext)
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider')
  }
  return context
}
